"use client";

/**
 * Floating WhatsApp Action Button — bottom-right of every page.
 *
 * A green circle with the official WhatsApp glyph that opens a chat with the
 * operator. Sits above page content but below modals (z-50).
 *
 * Overlap handling: listing detail pages (/listings/[slug]) render their own
 * mobile sticky "Request to book" bar pinned to bottom-0, and experience detail
 * pages (/experiences/[slug]) render the booking card's own "EGP X · Reserve"
 * bar, and /furnish/packages pins a price bar the same way. On those pages, on
 * mobile, the FAB lifts above that bar so the two don't collide. On every
 * other page (and on desktop, which has no bottom bar) it sits at the normal
 * bottom-right corner.
 */

// next-intl's usePathname returns the path WITHOUT the locale prefix
// (e.g. "/listings/x" on both / and /ar), so the listing-page check below
// works in Arabic too. next/navigation's usePathname would return
// "/ar/listings/x" and the startsWith("/listings/") check would miss.
import { usePathname } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { WHATSAPP_NUMBER } from "@/lib/site-config";

interface Props {
  /** Optional message to pre-fill in the WhatsApp chat. */
  message?: string;
}

export default function WhatsAppFAB({ message }: Props) {
  const pathname = usePathname();
  const t = useTranslations("whatsapp");
  // Pages that render their own mobile sticky bottom bar: listing detail pages
  // ("Request to book") and experience detail pages (the booking card's
  // "EGP X · Reserve" bar in MobileBar.tsx). `/experiences` itself has none, so
  // the trailing slash matters.
  const hasMobileBottomBar =
    pathname?.startsWith("/listings/") ||
    pathname?.startsWith("/experiences/") ||
    pathname === "/furnish/packages" ||
    false;

  // /checkout pins its own pay bar on phones and already offers WhatsApp in
  // its copy; a floating bubble over the total is the last thing it needs.
  if (pathname?.startsWith("/checkout")) return null;

  const href = message
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`
    : `https://wa.me/${WHATSAPP_NUMBER}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("chatAriaLabel")}
      title={t("chatAriaLabel")}
      className={`fixed end-5 md:end-6 md:bottom-6 z-50 w-14 h-14 md:w-[60px] md:h-[60px] rounded-full bg-[#25d366] hover:bg-[#1ebe5a] shadow-[0_8px_24px_-6px_rgba(37,211,102,0.45)] flex items-center justify-center transition-colors group ${
        // On mobile, lift above the ~72px listing-page CTA bar so the FAB
        // doesn't cover the "Request to book" button. Desktop is unaffected
        // (md:bottom-6 always wins there — no bottom bar exists).
        hasMobileBottomBar ? "bottom-[88px]" : "bottom-5"
      }`}
    >
      <svg
        width="30"
        height="30"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-white group-hover:scale-110 transition-transform"
        aria-hidden="true"
      >
        <path d="M20.52 3.48A11.94 11.94 0 0012.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.36-1.66a11.86 11.86 0 005.68 1.45h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.45zM12.05 21.79h-.01a9.86 9.86 0 01-5.03-1.38l-.36-.21-3.77.99 1.01-3.68-.24-.38a9.84 9.84 0 01-1.51-5.26c0-5.44 4.43-9.87 9.88-9.87 2.64 0 5.12 1.03 6.98 2.9a9.81 9.81 0 012.89 6.98c0 5.44-4.43 9.87-9.84 9.87zm5.41-7.39c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.76-1.66-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5l-.57-.01a1.1 1.1 0 00-.79.37c-.27.3-1.04 1.01-1.04 2.47s1.07 2.87 1.21 3.07c.15.2 2.1 3.21 5.08 4.5.71.31 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
      </svg>
    </a>
  );
}
